import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Languages as LanguagesIcon, Wand2, Loader2, Share2, Check } from "lucide-react";
import CommentsSection from "../components/CommentsSection";
import BlogReader from "../components/BlogReader";
import { langByCode, CATEGORIES, LANGUAGES } from "../lib/blogHelpers";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Indian languages available for reader-requested live translation.
// Subset of LANGUAGES + a few extras the backend supports.
const READER_TRANSLATE_LANGS = [
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "mr", name: "Marathi", native: "मराठी" },
  { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ur", name: "Urdu", native: "اردو" },
  { code: "or", name: "Odia", native: "ଓଡ଼ିଆ" },
  { code: "as", name: "Assamese", native: "অসমীয়া" },
];

const TAB_READER = "__reader__";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeLang, setActiveLang] = useState(null);

  // Reader-requested translation state
  const [readerTargetLang, setReaderTargetLang] = useState("");
  const [readerData, setReaderData] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const [readerError, setReaderError] = useState("");

  // Share state
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/blogs/${slug}`);
        setBlog(data);
        const langs = Object.keys(data.translations || {});
        const stored = localStorage.getItem("preferred_blog_lang");
        const initial =
          langs.includes(stored)
            ? stored
            : langs.includes(data.originalLang)
            ? data.originalLang
            : langs.includes("en")
            ? "en"
            : langs[0];
        setActiveLang(initial);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        else logError("Failed to load blog:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Disable copy / right-click / common screenshot shortcuts while on this page.
  useEffect(() => {
    const block = (e) => {
      e.preventDefault();
      return false;
    };
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      // Copy
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "u", "s", "p"].includes(k)) {
        e.preventDefault();
      }
      // Windows screen-snipping (Win + Shift + S)
      if (e.shiftKey && (e.metaKey || e.getModifierState?.("OS")) && k === "s") {
        e.preventDefault();
      }
      // PrintScreen — best effort (most browsers can't actually block)
      if (k === "printscreen") {
        navigator.clipboard?.writeText?.("");
      }
    };
    document.addEventListener("copy", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("dragstart", block);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("dragstart", block);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  if (notFound) return <Navigate to="/blogs" replace />;

  if (loading || !blog || !activeLang) {
    return (
      <div className="container-narrow py-32 text-center text-[#5C5852] italic">
        Loading…
      </div>
    );
  }

  const langs = Object.keys(blog.translations || {});
  const tr = blog.translations[activeLang] || {};
  const cat = CATEGORIES.find((c) => c.code === blog.category);
  const isReaderTab = activeLang === TAB_READER;
  const originalLang = blog.originalLang || "en";

  const switchLanguage = (code) => {
    setActiveLang(code);
    if (code !== TAB_READER) localStorage.setItem("preferred_blog_lang", code);
  };

  const requestReaderTranslation = async () => {
    if (!readerTargetLang) {
      setReaderError("Please pick a language.");
      return;
    }
    setReaderError("");
    setReaderLoading(true);
    setReaderData(null);
    try {
      const { data } = await axios.post(`${API}/translate`, {
        slug: blog.slug,
        fromLang: originalLang,
        toLang: readerTargetLang,
      });
      setReaderData(data);
      setActiveLang(TAB_READER);
    } catch (e) {
      logError("Reader translation failed:", e);
      setReaderError(
        e?.response?.data?.detail || "Translation service is busy. Please try again."
      );
    } finally {
      setReaderLoading(false);
    }
  };

  // Build a virtual blog object for the reader-tab so BlogReader can render it.
  const readerBlog = readerData
    ? {
        ...blog,
        translations: {
          ...blog.translations,
          [TAB_READER]: {
            title: readerData.title,
            synopsis: readerData.synopsis,
            story: readerData.story,
          },
        },
      }
    : blog;

  const heroTr =
    isReaderTab && readerData
      ? { title: readerData.title }
      : tr;

  // Share URL — backend serves blog-specific OG preview, then redirects to canonical
  const shareUrl = `${window.location.origin}/api/share/blogs/${blog.slug}`;
  const handleShare = async () => {
    const title = blog.translations?.en?.title || tr.title || "A Quiet Trail";
    try {
      if (navigator.share) {
        await navigator.share({ title, url: shareUrl });
        return;
      }
    } catch {
      // user cancelled — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) {
      logError("Share failed:", e);
    }
  };

  return (
    <div data-testid="page-blog-detail" className="blog-protect">
      {/* Hero */}
      <section className="relative">
        {blog.coverImage && (
          <div className="relative h-[50vh] sm:h-[60vh] min-h-[360px] overflow-hidden">
            <img
              src={blog.coverImage}
              alt={heroTr.title}
              className="w-full h-full object-cover no-copy"
              draggable="false"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(44,42,40,0.15) 0%, rgba(44,42,40,0.55) 70%, rgba(44,42,40,0.85) 100%)",
              }}
            />
            <div className="absolute inset-x-0 bottom-0">
              <div className="container-narrow pb-10 sm:pb-16">
                {cat && (
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="eyebrow !text-[#CC7A52]"
                  >
                    Blog · {cat.name}
                  </motion.p>
                )}
                <motion.h1
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                  className="font-serif text-[#FAF9F6] mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl"
                  data-testid="blog-title"
                  key={activeLang + "-title"}
                >
                  {heroTr.title}
                </motion.h1>
                <p className="mt-4 text-[#EAE5DC] text-sm" data-testid="blog-author">
                  Author: {blog.authorName || "Rajib Sengupta"}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-[#EAE5DC] text-sm">
                  {blog.publishedAt && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={14} strokeWidth={1.7} />
                      {new Date(blog.publishedAt).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {!blog.coverImage && (
          <div className="container-narrow pt-20 pb-6">
            {cat && <p className="eyebrow">Blog · {cat.name}</p>}
            <h1
              className="font-serif mt-3 text-5xl sm:text-6xl text-[#2C2A28] leading-tight"
              data-testid="blog-title"
            >
              {heroTr.title}
            </h1>
            <p className="mt-3 text-sm text-[#5C5852]" data-testid="blog-author">
              Author: {blog.authorName || "Rajib Sengupta"}
            </p>
          </div>
        )}
      </section>

      <section className="pt-8">
        <div className="container-narrow flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
            data-testid="blog-back"
          >
            <ArrowLeft size={15} strokeWidth={1.8} />
            All posts
          </Link>

          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-[#FAF9F6] transition-colors text-xs tracking-wide"
            data-testid="blog-share-button"
            title="Copy a rich-preview share link for WhatsApp, Facebook, etc."
          >
            {shareCopied ? (
              <>
                <Check size={13} />
                Share link copied
              </>
            ) : (
              <>
                <Share2 size={13} />
                Share this post
              </>
            )}
          </button>
        </div>
      </section>

      {/* Language tabs */}
      <section className="pt-8">
        <div className="container-narrow flex flex-wrap items-center gap-3">
          {langs.length > 1 && (
            <div
              className="inline-flex items-center gap-1 bg-[#F2EFEB] border border-[#D8D3CC] rounded-sm p-1"
              data-testid="blog-language-tabs"
            >
              <LanguagesIcon size={14} className="text-[#5C5852] ml-2 mr-1" strokeWidth={1.7} />
              {langs.map((code) => {
                const meta = langByCode(code);
                const active = code === activeLang;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => switchLanguage(code)}
                    className={`px-3 py-1.5 rounded-sm text-sm tracking-wide transition-colors ${
                      active
                        ? "bg-[#4A6B53] text-[#FAF9F6]"
                        : "text-[#2C2A28] hover:text-[#4A6B53]"
                    }`}
                    data-testid={`blog-lang-${code}`}
                  >
                    {meta.native}
                  </button>
                );
              })}
              {/* Reader-translated tab (only after a translation has been requested) */}
              {readerData && (
                <button
                  type="button"
                  onClick={() => setActiveLang(TAB_READER)}
                  className={`px-3 py-1.5 rounded-sm text-sm tracking-wide transition-colors inline-flex items-center gap-1.5 ${
                    isReaderTab
                      ? "bg-[#CC7A52] text-[#FAF9F6]"
                      : "text-[#CC7A52] hover:bg-[#CC7A52]/10"
                  }`}
                  data-testid="blog-lang-reader"
                >
                  <Wand2 size={12} />
                  {langByCode(readerData.toLang).native}
                </button>
              )}
            </div>
          )}

          {/* Reader-requested translation dropdown */}
          <div
            className="inline-flex items-center gap-2 text-sm"
            data-testid="reader-translate-row"
          >
            <span className="text-[#5C5852] hidden sm:inline">
              Read in your language:
            </span>
            <select
              value={readerTargetLang}
              onChange={(e) => setReaderTargetLang(e.target.value)}
              className="bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-1.5 text-sm text-[#2C2A28] field-ring"
              data-testid="reader-translate-lang-select"
            >
              <option value="">Choose a language…</option>
              {READER_TRANSLATE_LANGS.filter((l) => !langs.includes(l.code)).map(
                (l) => (
                  <option key={l.code} value={l.code}>
                    {l.native} ({l.name})
                  </option>
                )
              )}
            </select>
            <button
              type="button"
              onClick={requestReaderTranslation}
              disabled={readerLoading || !readerTargetLang}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#CC7A52] text-[#FAF9F6] hover:bg-[#A85F3D] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reader-translate-go"
            >
              {readerLoading ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  Translating
                </>
              ) : (
                <>
                  <Wand2 size={13} />
                  Translate
                </>
              )}
            </button>
          </div>
        </div>
        {readerError && (
          <div className="container-narrow mt-3">
            <p className="text-xs text-[#CC4A52]" data-testid="reader-translate-error">
              {readerError}
            </p>
          </div>
        )}
      </section>

      {/* Body */}
      <div className="pt-8">
        <BlogReader
          blog={readerBlog}
          activeLang={activeLang}
          readerMode={
            isReaderTab && readerData
              ? { fromLang: readerData.fromLang }
              : null
          }
        />
      </div>

      {/* Comments */}
      <section className="pb-20">
        <div className="container-narrow border-t border-[#D8D3CC] pt-10">
          <CommentsSection
            page="Blogs"
            itemId={blog.slug}
            itemTitle={blog.translations?.en?.title || tr.title}
          />
          <div className="mt-10">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-1.5 text-[#4A6B53] hover:text-[#3A5441] transition-colors"
              data-testid="blog-back-bottom"
            >
              <ArrowLeft size={16} strokeWidth={1.8} />
              Back to all posts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
